import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';

import type { PublicOrganizationWithBranchesItem } from '../widget-api/public-widget';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { displayableMediaUrl } from '../widget-lib/media-url';
import { PATH } from '../widget-lib/path-routes';
import { plainTextSnippet } from '../widget-lib/plain-text-snippet';
import { MediaAvatar } from '../widget-ui/media-avatar';

type Props = {
  org: PublicOrganizationWithBranchesItem;
};

/**
 * Шапка витрины глобального виджета: размытое фото, лого-иконка + название + описание.
 */
export function EmbedGuestShowcaseHeroHeader({ org }: Props) {
  const [heroFailed, setHeroFailed] = useState(false);
  const suffix = useQuerySuffix();
  const heroSrc = displayableMediaUrl(org.photo_url);
  const showHeroBg = heroSrc != null && !heroFailed;
  const name = (org.name || '').trim() || 'Студия';
  const desc = plainTextSnippet(org.description, 280);
  const homeTo = `${PATH.home}${suffix}`;
  useEffect(() => {
    setHeroFailed(false);
  }, [heroSrc]);

  return (
    <div
      className={`relative h-16 overflow-hidden sm:h-[4.5rem] ${showHeroBg ? '' : 'bg-[var(--accent-bg)]'}`}
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      {showHeroBg ? (
        <img
          src={heroSrc}
          alt=""
          className="absolute inset-0 h-full w-full scale-110 object-cover blur-sm"
          sizes="100vw"
          decoding="async"
          onError={() => setHeroFailed(true)}
        />
      ) : null}
      <div
        className={`pointer-events-none absolute inset-0 ${showHeroBg ? 'bg-[var(--bg-elevated)]/55' : ''}`}
        aria-hidden
      />

      <NavLink
        to={homeTo}
        className="relative z-[1] flex h-full items-center gap-3 px-3 sm:px-5 transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent-fg)]"
        aria-label={`${name} — главная страница виджета`}
      >
        <MediaAvatar
          photoUrl={org.brand_logo_url}
          label={name}
          sizeClass="h-10 w-10 shrink-0 sm:h-11 sm:w-11"
          roundedClass="rounded-xl ring-2 ring-white/20 shadow-lg"
        />
        <div className="min-w-0 flex-1">
          <h1
            className={`truncate text-sm font-semibold tracking-tight sm:text-base ${
              showHeroBg ? 'text-[var(--text)]' : 'text-[var(--accent-fg)]'
            }`}
          >
            {name}
          </h1>
          {desc ? (
            <p
              className={`mt-0.5 line-clamp-1 text-xs leading-snug ${
                showHeroBg ? 'text-[var(--text-muted)]' : 'text-[var(--accent-fg)]'
              }`}
            >
              {desc}
            </p>
          ) : null}
        </div>
      </NavLink>
    </div>
  );
}
